import { useIntl } from '../../util/reactIntl';
import { NamedLink } from '..';
import { ensureListing } from '../../util/data';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createSlug } from '../../util/urlHelpers';
import { formatMoney } from '../../util/currency';
import { cleanListingTitle, decodeHtmlEntities, formatSize } from '../../util/string';
import fallbackImage from '../../containers/ResalePage/images/image5.png';

import css from './MarketplaceCard.module.css';

const { Money } = sdkTypes;

const getListingImage = listing => {
  const firstImage = listing.images && listing.images.length > 0 ? listing.images[0] : null;
  const variants = firstImage?.attributes?.variants || {};

  return (
    variants['scaled-medium']?.url ||
    variants['listing-card']?.url ||
    variants['scaled-small']?.url ||
    firstImage?.attributes?.url ||
    null
  );
};

const isSoldItemRecord = listing => {
  const hasListingAttributes = !!listing?.attributes;
  return !hasListingAttributes && !!(listing?.title || listing?.brandName || listing?.imageUrl);
};

const ensureMoney = value => {
  if (!value) {
    return null;
  }

  if (value instanceof Money) {
    return value;
  }

  if (value?.amount != null && value?.currency) {
    return new Money(value.amount, value.currency);
  }

  return null;
};

const normalizeText = value =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const pickBrandName = ({ rawTitle, candidates }) => {
  const decodedTitle = decodeHtmlEntities(String(rawTitle || '')).trim();
  const normalizedTitle = normalizeText(decodedTitle);

  const cleanedCandidates = (candidates || [])
    .map(candidate => decodeHtmlEntities(String(candidate || '')).trim())
    .filter(Boolean);

  const firstValid = cleanedCandidates.find(candidate => {
    const normalizedCandidate = normalizeText(candidate);

    // Some listings incorrectly store the full title in the brand field.
    if (normalizedCandidate && normalizedCandidate === normalizedTitle) {
      return false;
    }

    return true;
  });

  if (firstValid) {
    return firstValid;
  }

  // Last-resort fallback: derive a simple brand token from title instead of showing a blank title.
  const titleLeadToken = decodedTitle.split(/\s+/).find(Boolean);
  return titleLeadToken || 'Unknown Brand';
};

const MarketplaceCard = ({ listing, variant = 'resale', size = 'default', className = '' }) => {
  const intl = useIntl();
  const soldItem = isSoldItemRecord(listing) ? listing : null;
  const ensured = soldItem ? null : ensureListing(listing);
  const attrs = ensured?.attributes || {};
  const publicData = attrs.publicData || {};

  const isReviewCard = variant === 'review';
  const isResaleSoldCard =
    variant === 'resale-sold' || variant === 'resaleSold' || variant === 'resale sold';
  const shouldLink = !isResaleSoldCard;

  const rawTitle = soldItem?.title || attrs.title;
  const brandName = pickBrandName({
    rawTitle,
    candidates: soldItem
      ? [soldItem.brandName]
      : [
          publicData.BrandName,
          publicData.brand,
          publicData.brandName,
          ensured?.attributes?.author?.attributes?.profile?.displayName,
        ],
  });
  const decodedTitle = decodeHtmlEntities(String(rawTitle || 'Untitled'));
  const cleanedTitle = cleanListingTitle(decodedTitle, brandName);
  const displayTitle = cleanedTitle || decodedTitle;
  const listingSize = formatSize(publicData);
  const height = publicData?.height || 'n/a';
  const moneyValue = soldItem ? ensureMoney(soldItem.price) : attrs.price;
  const price = moneyValue ? formatMoney(intl, moneyValue) : null;

  const imageUrl = soldItem?.imageUrl || getListingImage(ensured);
  const id = shouldLink ? ensured?.id?.uuid : null;
  const slug = shouldLink
    ? isReviewCard
      ? publicData.slug || createSlug(attrs.title)
      : createSlug(attrs.title)
    : null;

  const linkName = isReviewCard ? 'ReviewDetailPage' : 'ProductPage';
  const linkParams = isReviewCard ? { slug, id: String(id) } : { id, slug };

  const isSmallSize = size === 'small' || size === 'small-short';
  const isShortSize = size === 'short' || size === 'small-short';
  const badgeLabel = isReviewCard ? 'REVIEW' : !isResaleSoldCard ? 'RESALE' : null;
  const imageLoading = isResaleSoldCard ? 'lazy' : undefined;

  const cardClasses = [
    css.card,
    isReviewCard ? css.reviewVariant : css.resaleVariant,
    isResaleSoldCard ? css.resaleSoldVariant : '',
    isSmallSize ? css.smallSize : '',
    isShortSize ? css.shortSize : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const cardContent = (
    <>
      <div className={css.imageWrapper}>
        <img
          src={imageUrl || fallbackImage}
          alt=""
          aria-hidden="true"
          className={css.imageBg}
          loading={imageLoading}
          decoding="async"
        />
        <img
          src={imageUrl || fallbackImage}
          alt={rawTitle || 'Product image'}
          className={css.image}
          loading={imageLoading}
          decoding="async"
        />
        {badgeLabel ? (
          <div className={css.cardLabel}>
            <span className={css.cardLabelText}>{badgeLabel}</span>
          </div>
        ) : null}
        {isResaleSoldCard ? (
          <div className={css.soldLabel}>
            <span className={css.soldLabelText}>SOLD</span>
          </div>
        ) : null}
      </div>

      <div className={css.content}>
        {isResaleSoldCard ? (
          <>
            <div className={css.brand}>{brandName?.toUpperCase?.() || brandName}</div>
            <div className={css.title}>{displayTitle}</div>
            <div className={css.metaRow}>
              {listingSize && listingSize !== 'N/A' ? (
                <span className={css.metaPrimary}>Size: {listingSize}</span>
              ) : null}
              {price ? <span className={css.metaSecondary}>{price}</span> : null}
            </div>
          </>
        ) : (
          <>
            <div className={css.brand}>{brandName?.toUpperCase?.() || brandName}</div>
            <div className={css.title}>{displayTitle}</div>

            <div className={css.metaRow}>
              <span className={css.metaPrimary}>Size: {listingSize}</span>
              {isReviewCard ? (
                <span className={css.metaSecondary}>Height: {height}</span>
              ) : price ? (
                <span className={css.metaSecondary}>{price}</span>
              ) : null}
            </div>
          </>
        )}
      </div>

      {!isReviewCard && !isResaleSoldCard ? null : null}
    </>
  );

  return shouldLink ? (
    <NamedLink className={cardClasses} name={linkName} params={linkParams}>
      {cardContent}
    </NamedLink>
  ) : (
    <div className={cardClasses}>{cardContent}</div>
  );
};

export default MarketplaceCard;
